import { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { MODULES, ACTIONS } from "../utils/permissions";
import { Can, DocumentTypeGuard } from "../components/PermissionGuard";
import { isExpired, formatDate, COLLECTIONS, REQUEST_STATUS } from "../utils";
import { useSortableTable, SORTABLE_COLUMNS } from "../hooks/useSortableTable";
import SortableHeader from "../components/SortableHeader";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";

const tabs = [
  { key: "all", label: "Todos" },
  { key: "pending", label: "Pendientes" },
  { key: "signed", label: "Firmados" },
  { key: "expired", label: "Expirados" },
];

// 🔧 Tabs para filtrar por tipo de documento
const typeTabs = [
  { key: "all", label: "Todos" },
  { key: "albaran", label: "📄 Albaranes" },
  { key: "parte_trabajo", label: "🔧 Partes" },
  { key: "prl", label: "🛡️ PRL" },
];

// Componente de tarjeta para móvil
function DocumentCard({ doc, expired, isSelectMode, isSelected, onToggle }) {
  return (
    <div className="doc-card" style={{ position: 'relative' }}>
      {isSelectMode && (
        <div 
          onClick={() => onToggle(doc.id)}
          style={{ 
            position: 'absolute', 
            top: 12, 
            left: 12, 
            zIndex: 10,
            width: 26,
            height: 26,
            border: '2px solid #3b82f6',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isSelected ? '#3b82f6' : '#fff',
            transition: 'all 0.15s',
            userSelect: 'none'
          }}
        >
          {isSelected && (
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style={{ pointerEvents: 'none' }}>
              <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}
      <div className="doc-card-header" style={{ paddingLeft: isSelectMode ? 40 : undefined }}>
        <h3 className="doc-card-title">{doc.originalName || "(sin nombre)"}</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* 🔧 Badge de tipo */}
          <span className="badge" style={{ 
            background: doc.documentType === "prl" ? "#fef3c7" : doc.documentType === "parte_trabajo" ? "#dcfce7" : "#dbeafe",
            color: doc.documentType === "prl" ? "#92400e" : doc.documentType === "parte_trabajo" ? "#166534" : "#1e40af",
            border: doc.documentType === "prl" ? "1px solid #fde68a" : doc.documentType === "parte_trabajo" ? "1px solid #bbf7d0" : "1px solid #bfdbfe",
            padding: "6px 12px",
            minWidth: "fit-content"
          }}>
            {doc.documentType === "prl" ? "🛡️ PRL" : doc.documentType === "parte_trabajo" ? "🔧 Parte de Trabajo" : "📄 Albarán"}
          </span>
          {/* Badge de estado */}
          {doc.status === REQUEST_STATUS.SIGNED ? (
            <span className="badge" style={{ background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}>firmado</span>
          ) : expired ? (
            <span className="badge" style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }}>expirado</span>
          ) : (
            <span className="badge" style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}>pendiente</span>
          )}
        </div>
      </div>
      
      <div className="doc-card-body">
        {doc.obra && (
          <div className="doc-card-row">
            <span className="meta">Obra:</span>
            <span className="doc-card-value" style={{ fontWeight: 500, color: "#c2410c" }}>{doc.obra}</span>
          </div>
        )}
        <div className="doc-card-row">
          <span className="meta">ID:</span>
          <span className="doc-card-value">{doc.id}</span>
        </div>
        <div className="doc-card-row">
          <span className="meta">Creado:</span>
          <span className="doc-card-value">{formatDate(doc.createdAt)}</span>
        </div>
        {doc.signedAt && (
          <div className="doc-card-row">
            <span className="meta">Firmado:</span>
            <span className="doc-card-value">{formatDate(doc.signedAt)}</span>
          </div>
        )}
        {doc.signerName && (
          <div className="doc-card-row">
            <span className="meta">Firmante:</span>
            <span className="doc-card-value">{doc.signerName} ({doc.signerDni || "-"})</span>
          </div>
        )}
      </div>
      
      <div className="doc-card-actions">
        {doc.status === REQUEST_STATUS.PENDING && !expired && (
          <Link className="copy" to={`/sign/${doc.id}`}>Firmar</Link>
        )}
        {doc.status === REQUEST_STATUS.SIGNED && (doc.signedURL || doc.downloadURL) && (
          <>
            <a className="copy" href={doc.signedURL || doc.downloadURL} target="_blank" rel="noreferrer">Abrir</a>
            <a className="copy" href={doc.signedURL || doc.downloadURL} download>Descargar</a>
          </>
        )}
      </div>
    </div>
  );
}

export default function SignedList() {
  const { companyId, loading: authLoading, claimsLoaded } = useAuth();
  const { 
    canAccessDocumentType, 
    getAllowedDocumentTypes,
    hasPermission,
    loading: permissionsLoading
  } = usePermissions();
  
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [activeType, setActiveType] = useState("all"); // 🔧 Filtro por tipo
  
  // 📦 Estado para selección múltiple
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectCounter, setSelectCounter] = useState(0); // Forzar re-render
  
  const [obraFilter, setObraFilter] = useState(""); // 🏗️ Filtro por obra
  const [dateField, setDateField] = useState("createdAt"); // Campo de fecha para filtrar
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // 🚀 HOTFIX: Estado para refresh manual
  const urlCache = useRef({});
  const prlDocsRef = useRef([]); // 🚀 HOTFIX: Referencia para PRLs
  const requestDocsRef = useRef([]); // 🚀 HOTFIX: Referencia para Requests
  const { sortColumn, sortDirection, handleSort, sortItems } = useSortableTable();

  // 🔄 HOTFIX: Función para refresh manual
  const handleRefresh = () => {
    setRefreshing(true);
    
    // Limpiar caché de URLs para forzar recarga
    urlCache.current = {};
    
    // Forzar re-render y mostrar loading temporal
    setLoading(true);
    
    // Simular un pequeño delay para UX
    setTimeout(() => {
      setLoading(false);
      setRefreshing(false);
    }, 500);
  };

  // Get allowed document types based on permissions
  const allowedTypes = getAllowedDocumentTypes();

  useEffect(() => {
    if (authLoading || !claimsLoaded) {
      console.log('⏳ [SignedList] Esperando auth/claims...');
      return;
    }

    console.log('🔍 [SignedList] Cargando documentos para companyId:', companyId || 'super_admin');

    const allDocs = [];
    const unsubscribers = [];

    // 📄 Suscripción a la colección "requests" (albaranes y partes de trabajo)
    const requestsQuery = companyId 
      ? query(
          collection(db, COLLECTIONS.REQUESTS), 
          where("companyId", "==", companyId),
          orderBy("createdAt", "desc")
        )
      : query(
          collection(db, COLLECTIONS.REQUESTS),
          orderBy("createdAt", "desc")
        );
    
    const unsubRequests = onSnapshot(
      requestsQuery,
      async (snap) => {
        // 🚀 HOTFIX: Mostrar documentos inmediatamente, cargar URLs en background
        const requestDocs = snap.docs.map((d) => {
          const r = { id: d.id, ...d.data() };
          
          // Marcar que necesita URL si está firmado
          if (r.status === REQUEST_STATUS.SIGNED && r.signedPath) {
            r._needsURL = true;
            r.signedURL = urlCache.current[r.signedPath] || null;
          }
          
          return r;
        });
        
        // Actualizar referencia y estado inmediatamente
        requestDocsRef.current = requestDocs;
        const combined = [...requestDocs, ...(prlDocsRef.current || [])];
        setItems(combined);
        setLoading(false);
        
        // 🔄 Cargar URLs en background (sin bloquear el render)
        requestDocs.forEach(async (r) => {
          if (r._needsURL && !urlCache.current[r.signedPath]) {
            try {
              const url = await getDownloadURL(ref(storage, r.signedPath));
              urlCache.current[r.signedPath] = url;
              
              // Actualizar solo este documento específico
              setItems(prev => prev.map(item => 
                item.id === r.id ? { ...item, signedURL: url } : item
              ));
            } catch (err) {
              console.warn('⚠️ Error cargando URL:', r.signedPath, err);
              urlCache.current[r.signedPath] = null;
            }
          }
        });

        // 🛡️ Suscripción a la colección "prl_requests" (documentos PRL)
        const prlQuery = companyId 
          ? query(
              collection(db, COLLECTIONS.PRL_REQUESTS), 
              where("companyId", "==", companyId),
              orderBy("createdAt", "desc")
            )
          : query(
              collection(db, COLLECTIONS.PRL_REQUESTS),
              orderBy("createdAt", "desc")
            );
        
        const unsubPRL = onSnapshot(
          prlQuery,
          async (prlSnap) => {
            // 🚀 HOTFIX: Mostrar PRLs inmediatamente, cargar URLs en background
            const prlDocs = prlSnap.docs.map((d) => {
              const prlData = { id: d.id, ...d.data() };
              
              // Marcar como tipo PRL y status signed
              prlData.documentType = "prl";
              prlData.status = "signed";
              prlData.originalName = prlData.originalName || `PRL_${prlData.obra || 'documento'}.pdf`;
              
              // Marcar que necesita URL si no es HTTP
              if (prlData.downloadURL && !prlData.downloadURL.startsWith('http')) {
                prlData._needsURL = true;
                prlData.downloadURL = urlCache.current[prlData.downloadURL] || null;
              }
              
              return prlData;
            });
            
            // Actualizar referencia y estado inmediatamente
            prlDocsRef.current = prlDocs;
            const combined = [...(requestDocsRef.current || []), ...prlDocs];
            setItems(combined);
            setLoading(false);
            
            // 🔄 Cargar URLs de PRL en background
            prlDocs.forEach(async (prl) => {
              if (prl._needsURL && !urlCache.current[prl.downloadURL]) {
                try {
                  const url = await getDownloadURL(ref(storage, prl.downloadURL));
                  urlCache.current[prl.downloadURL] = url;
                  
                  // Actualizar solo este documento específico
                  setItems(prev => prev.map(item => 
                    item.id === prl.id ? { ...item, downloadURL: url } : item
                  ));
                } catch (err) {
                  console.warn('⚠️ Error cargando URL PRL:', prl.downloadURL, err);
                  urlCache.current[prl.downloadURL] = null;
                }
              }
            });
          },
          (err) => {
            console.error('❌ Error cargando PRLs:', err);
            // Si falla PRLs, al menos mostrar requests
            setItems(requestDocs);
            setLoading(false);
          }
        );

        unsubscribers.push(unsubPRL);
      },
      (err) => {
        console.error('❌ Error cargando requests:', err);
        setLoading(false);
      }
    );

    unsubscribers.push(unsubRequests);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [companyId, authLoading, claimsLoaded]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      // Si los permisos aún cargan, mostrar todos los documentos sin filtrar por tipo
      if (permissionsLoading) return true;
      if (!canAccessDocumentType(it.documentType || 'albaran')) {
        return false;
      }
      
      const expired = isExpired(it.expiresAt);
      
      // Filtro por estado
      let passesStatusFilter = false;
      if (active === "all") passesStatusFilter = true;
      else if (active === "pending") passesStatusFilter = it.status === REQUEST_STATUS.PENDING && !expired;
      else if (active === "signed") passesStatusFilter = it.status === REQUEST_STATUS.SIGNED;
      else if (active === "expired") passesStatusFilter = expired && it.status !== REQUEST_STATUS.SIGNED;
      
      if (!passesStatusFilter) return false;
      
      // 🔧 Filtro por tipo de documento
      if (activeType !== "all") {
        if (activeType === "albaran" && it.documentType && it.documentType !== "albaran") return false;
        if (activeType === "parte_trabajo" && it.documentType !== "parte_trabajo") return false;
        if (activeType === "prl" && it.documentType !== "prl") return false;
      }
      
      // 🏗️ Filtro por obra (búsqueda en tiempo real)
      if (obraFilter.trim()) {
        const obraText = (it.obra || "").toLowerCase();
        const filterText = obraFilter.toLowerCase();
        if (!obraText.includes(filterText)) return false;
      }
      
      // 📅 Filtro por rango de fechas
      if (dateFrom || dateTo) {
        const fieldVal = it[dateField];
        let dateMs = 0;
        if (fieldVal) {
          if (typeof fieldVal.toMillis === "function") dateMs = fieldVal.toMillis();
          else if (typeof fieldVal.seconds === "number") dateMs = fieldVal.seconds * 1000;
        }
        if (!dateMs) return false; // sin fecha → no pasa el filtro
        if (dateFrom) {
          const fromMs = new Date(dateFrom).getTime();
          if (dateMs < fromMs) return false;
        }
        if (dateTo) {
          const toMs = new Date(dateTo).getTime() + 86400000 - 1; // fin del día
          if (dateMs > toMs) return false;
        }
      }
      
      return true;
    });
  }, [items, active, activeType, obraFilter, dateField, dateFrom, dateTo, canAccessDocumentType]);

  const sorted = useMemo(() => sortItems(filtered), [filtered, sortColumn, sortDirection]);

  // 📦 Funciones para selección múltiple
  const toggleSelectDoc = (docId) => {
    console.log('🔘 Toggle doc:', docId);
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        console.log('  ➡ Deseleccionando');
        newSet.delete(docId);
      } else {
        console.log('  ➡ Seleccionando');
        newSet.add(docId);
      }
      console.log('  ➡ Total seleccionados:', newSet.size);
      return newSet;
    });
    setSelectCounter(c => c + 1); // Forzar re-render
  };

  const toggleSelectAll = () => {
    console.log('🔘 Toggle All - Filtered docs:', filtered.length, 'Selected:', selectedDocs.size);
    if (selectedDocs.size === filtered.length && filtered.length > 0) {
      console.log('  ➡ Deseleccionar todos');
      setSelectedDocs(new Set());
    } else {
      console.log('  ➡ Seleccionar todos');
      const allIds = filtered.map(doc => doc.id);
      console.log('  ➡ IDs:', allIds);
      setSelectedDocs(new Set(allIds));
    }
    setSelectCounter(c => c + 1); // Forzar re-render
  };

  const downloadSelected = async () => {
    const selectedDocuments = filtered.filter(doc => selectedDocs.has(doc.id));
    
    if (selectedDocuments.length === 0) {
      alert('No hay documentos seleccionados');
      return;
    }

    let downloaded = 0;
    for (const doc of selectedDocuments) {
      const url = doc.signedURL || doc.downloadURL;
      if (url) {
        try {
          // Descargar usando fetch + blob para forzar descarga real
          const response = await fetch(url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = doc.originalName || `documento_${doc.id}.pdf`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Liberar el blob URL
          window.URL.revokeObjectURL(blobUrl);
          
          downloaded++;
          console.log(`✅ Descargado: ${doc.originalName}`);
          
          // Esperar un poco entre descargas para no saturar
          await new Promise(resolve => setTimeout(resolve, 800));
        } catch (err) {
          console.error(`❌ Error descargando ${doc.originalName}:`, err);
        }
      }
    }
    
    alert(`✅ ${downloaded} de ${selectedDocuments.length} documentos descargados`);
    
    // Limpiar selección después de descargar
    setSelectedDocs(new Set());
    setIsSelectMode(false);
  };
  const { paginated, page, pageSize, totalPages, totalItems, goTo, changePageSize } = usePagination(sorted);

  return (
    <div className="container">
      <div className="header">
        <span className="badge">Dashboard</span>
      </div>

      <h1 className="title">Documentos</h1>

      {/* Tabs de estado */}
      <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap", background: "#e9eef5", borderRadius: 10, padding: 4, width: "fit-content" }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            style={{
              padding: "6px 18px",
              borderRadius: 7,
              border: "none",
              background: active === t.key ? "#ffffff" : "transparent",
              color: active === t.key ? "#0f172a" : "#64748b",
              fontWeight: active === t.key ? 600 : 400,
              fontSize: 13,
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: active === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tabs de tipo de documento */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap", background: "#e9eef5", borderRadius: 10, padding: 4, width: "fit-content" }}>
        {typeTabs
          .filter(tab => tab.key === "all" || allowedTypes.includes(tab.key))
          .map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveType(t.key)}
              style={{
                padding: "6px 18px",
                borderRadius: 7,
                border: "none",
                background: activeType === t.key ? "#ffffff" : "transparent",
                color: activeType === t.key ? "#0f172a" : "#64748b",
                fontWeight: activeType === t.key ? 600 : 400,
                fontSize: 13,
                cursor: "pointer",
                transition: "all 0.15s",
                boxShadow: activeType === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        {allowedTypes.length > 0 && allowedTypes.length < 2 && (
          <div style={{ padding: "8px 12px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "6px", fontSize: "12px", color: "#0369a1" }}>
            ℹ️ Solo puedes ver: {allowedTypes.map(type => type === 'albaran' ? 'Albaranes' : 'Partes de Trabajo').join(', ')}
          </div>
        )}
      </div>

      {/* 🏗️ Búsqueda por obra */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="text"
            placeholder="🔍 Buscar por obra..."
            value={obraFilter}
            onChange={(e) => setObraFilter(e.target.value)}
            style={{ width: "100%", maxWidth: "400px", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
          />
        </div>
        {obraFilter && (
          <div style={{ marginTop: 8, fontSize: "14px", color: "#64748b" }}>
            Mostrando {filtered.length} documento{filtered.length !== 1 ? 's' : ''} con "{obraFilter}"
          </div>
        )}
      </div>

      {/* 📅 Filtro por rango de fechas */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <select value={dateField} onChange={(e) => setDateField(e.target.value)} style={{ width: 'auto', background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14, cursor: 'pointer' }}>
          <option value="createdAt">Fecha Creado</option>
          <option value="signedAt">Fecha Firmado</option>
        </select>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "#64748b" }}>Desde:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ width: 'auto', background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }} />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "#64748b" }}>Hasta:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ width: 'auto', background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }} />
        </div>
        {(dateFrom || dateTo) && (
          <button className="copy" onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ padding: "9px 14px", fontSize: 13 }}>
            ✕ Limpiar fechas
          </button>
        )}
      </div>

      {/* 📦 Controles de selección múltiple y refresh */}
      <div style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <button 
            className="copy" 
            onClick={() => {
              setIsSelectMode(!isSelectMode);
            if (isSelectMode) setSelectedDocs(new Set());
          }}
          style={{ 
            padding: "9px 18px", 
            fontSize: 14,
            background: isSelectMode ? "#3b82f6" : "#fff",
            color: isSelectMode ? "#fff" : "#111827",
            border: isSelectMode ? "none" : "1px solid #d1d5db"
          }}
        >
          {isSelectMode ? "✓ Modo Selección" : "☐ Modo Selección"}
        </button>
        
        {isSelectMode && filtered.length > 0 && (
          <>
            <button 
              className="copy" 
              onClick={toggleSelectAll}
              style={{ padding: "9px 18px", fontSize: 14 }}
            >
              {selectedDocs.size === filtered.length ? "☐ Deseleccionar Todos" : "☑ Seleccionar Todos"}
            </button>
            
            {selectedDocs.size > 0 && (
              <>
                <div style={{ 
                  padding: "9px 18px", 
                  background: "#dbeafe", 
                  border: "1px solid #93c5fd", 
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1e40af"
                }}>
                  {selectedDocs.size} documento{selectedDocs.size !== 1 ? 's' : ''} seleccionado{selectedDocs.size !== 1 ? 's' : ''}
                </div>
                
                <button 
                  className="copy" 
                  onClick={downloadSelected}
                  style={{ 
                    padding: "9px 18px", 
                    fontSize: 14,
                    background: "#10b981",
                    color: "#fff",
                    border: "none",
                    fontWeight: 600
                  }}
                >
                  ⬇ Descargar Seleccionados
                </button>
              </>
            )}
          </>
        )}
        </div>
        
        {/* 🔄 HOTFIX: Botón de refresh manual */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button 
            className="copy" 
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ 
              padding: "9px 18px", 
              fontSize: 14,
              background: refreshing ? "#e5e7eb" : "#f3f4f6",
              color: refreshing ? "#9ca3af" : "#374151",
              border: "1px solid #d1d5db",
              cursor: refreshing ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
          >
            <span style={{ 
              display: "inline-block", 
              animation: refreshing ? "spin 1s linear infinite" : "none"
            }}>
              🔄
            </span>
            {refreshing ? "Actualizando..." : "Actualizar"}
          </button>
          
          {/* Indicador de documentos con URLs pendientes */}
          {items.some(item => item._needsURL && !item.signedURL && !item.downloadURL) && (
            <div style={{ 
              padding: "6px 12px", 
              background: "#fef3c7", 
              border: "1px solid #fde68a", 
              borderRadius: "6px",
              fontSize: "12px",
              color: "#92400e",
              display: "flex",
              alignItems: "center",
              gap: "4px"
            }}>
              <span style={{ animation: "pulse 2s ease-in-out infinite" }}>⏳</span>
              Cargando enlaces...
            </div>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p className="meta">Cargando…</p>
        ) : allowedTypes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '48px 24px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
            <h3 style={{ color: '#dc2626', marginBottom: '8px' }}>Sin permisos para ver documentos</h3>
            <p style={{ color: '#7f1d1d', margin: 0 }}>
              No tienes permisos para ver albaranes ni partes de trabajo. Contacta con tu administrador.
            </p>
          </div>
        ) : sorted.length === 0 ? (
          <p className="meta">No hay documentos para mostrar.</p>
        ) : (
          <>
            {/* Vista de tabla (desktop) */}
            <div className="doc-list-table">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr className="meta" style={{ textAlign: "left" }}>
                    {isSelectMode && (
                      <th style={{ padding: "8px 10px", width: "50px" }}>
                        <div 
                          onClick={toggleSelectAll}
                          style={{
                            width: 24,
                            height: 24,
                            border: '2px solid #3b82f6',
                            borderRadius: 4,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: (selectedDocs.size === filtered.length && filtered.length > 0) ? '#3b82f6' : '#fff',
                            transition: 'all 0.15s',
                            userSelect: 'none'
                          }}
                        >
                          {(selectedDocs.size === filtered.length && filtered.length > 0) && (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ pointerEvents: 'none' }}>
                              <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                      </th>
                    )}
                    {SORTABLE_COLUMNS.map((col) => (
                      <SortableHeader
                        key={col.key}
                        label={col.label}
                        columnKey={col.key}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        style={{ padding: "8px 10px" }}
                      />
                    ))}
                    <th style={{ padding: "8px 10px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((r) => {
                    const created = formatDate(r.createdAt);
                    const signedAt = formatDate(r.signedAt);
                    const expired = isExpired(r.expiresAt);

                    return (
                      <tr key={r.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                        {isSelectMode && (
                          <td style={{ padding: "10px" }}>
                            <div 
                              onClick={() => toggleSelectDoc(r.id)}
                              style={{
                                width: 24,
                                height: 24,
                                border: '2px solid #3b82f6',
                                borderRadius: 4,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: selectedDocs.has(r.id) ? '#3b82f6' : '#fff',
                                transition: 'all 0.15s',
                                userSelect: 'none'
                              }}
                            >
                              {selectedDocs.has(r.id) && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ pointerEvents: 'none' }}>
                                  <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                          </td>
                        )}
                        <td style={{ padding: "10px" }}>
                          <div style={{ fontWeight: 600 }}>{r.originalName || "(sin nombre)"}</div>
                          <div className="meta" style={{ opacity: 0.8 }}>id: {r.id}</div>
                        </td>
                        <td style={{ padding: "10px" }}>
                          <div style={{ fontWeight: 500, color: "#c2410c" }}>
                            {r.obra || "-"}
                          </div>
                        </td>
                        <td style={{ padding: "10px" }}>
                          <span className="badge" style={{ 
                            background: r.documentType === "prl" ? "#fef3c7" : r.documentType === "parte_trabajo" ? "#dcfce7" : "#dbeafe",
                            color: r.documentType === "prl" ? "#92400e" : r.documentType === "parte_trabajo" ? "#166534" : "#1e40af",
                            border: r.documentType === "prl" ? "1px solid #fde68a" : r.documentType === "parte_trabajo" ? "1px solid #bbf7d0" : "1px solid #bfdbfe",
                            padding: "4px 10px",
                            whiteSpace: "nowrap"
                          }}>
                            {r.documentType === "prl" ? "🛡️ PRL" : r.documentType === "parte_trabajo" ? "🔧 Parte de Trabajo" : "📄 Albarán"}
                          </span>
                        </td>
                        <td style={{ padding: "10px" }}>
                          {r.status === REQUEST_STATUS.SIGNED ? (
                            <span className="badge" style={{ background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}>firmado</span>
                          ) : expired ? (
                            <span className="badge" style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }}>expirado</span>
                          ) : (
                            <span className="badge" style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}>pendiente</span>
                          )}
                        </td>
                        <td style={{ padding: "10px" }}>{created}</td>
                        <td style={{ padding: "10px" }}>{signedAt}</td>
                        <td style={{ padding: "10px" }}>
                          {r.signerName ? `${r.signerName} (${r.signerDni || "-"})` : "-"}
                        </td>
                        <td style={{ padding: "10px", display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {r.status === REQUEST_STATUS.PENDING && !expired && (
                            <Link className="copy" to={`/sign/${r.id}`}>Firmar</Link>
                          )}
                          {r.status === REQUEST_STATUS.SIGNED && (r.signedURL || r.downloadURL) && (
                            <>
                              <a className="copy" href={r.signedURL || r.downloadURL} target="_blank" rel="noreferrer">Abrir</a>
                              <a className="copy" href={r.signedURL || r.downloadURL} download>Descargar</a>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Vista de cards (mobile) */}
            <div className="doc-card-list">
              {paginated.map((r) => {
                const expired = isExpired(r.expiresAt);
                return <DocumentCard 
                  key={r.id} 
                  doc={r} 
                  expired={expired}
                  isSelectMode={isSelectMode}
                  isSelected={selectedDocs.has(r.id)}
                  onToggle={toggleSelectDoc}
                />;
              })}
            </div>
            <Pagination page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems} goTo={goTo} changePageSize={changePageSize} />
          </>
        )}
      </div>
    </div>
  );
}

// 🚀 HOTFIX: Estilos CSS para animaciones
const styles = `
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
`;

// Inyectar estilos si no existen
if (typeof document !== 'undefined' && !document.getElementById('signedlist-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'signedlist-styles';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
